import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconImage, IconUpload, IconDelete, IconSuccess } from '../../../icons';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebaseConfig';
import { useAuth } from '../../../context/AuthContext';
import logger from '../../../utils/logger';

interface LogoStepProps {
  initialLogoUrl: string | null;
  onSave: (data: { logoUrl: string | null }) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

const LogoStep: React.FC<LogoStepProps> = ({
  initialLogoUrl,
  onSave,
  onNext,
  onBack,
  onSkip,
}) => {
  const { t } = useTranslation('onboarding');
  const { currentUser } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    // Validation
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('steps.logo.errorType'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError(t('steps.logo.errorSize'));
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const extension = file.name.substring(file.name.lastIndexOf('.'));
      const storagePath = `logos/${currentUser.uid}/logo${extension}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (uploadError) => {
          logger.error(uploadError, 'Logo upload error');
          setError(t('steps.logo.errorUpload'));
          setIsUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setLogoUrl(downloadURL);
          setIsUploading(false);
          
          // Auto-save after successful upload
          try {
            await onSave({ logoUrl: downloadURL });
          } catch (saveErr) {
            logger.error(saveErr, 'Error saving logo URL');
          }
        }
      );
    } catch (err) {
      logger.error(err, 'Logo upload error');
      setError(t('steps.logo.errorUpload'));
      setIsUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave({ logoUrl });
      onNext();
    } catch (err) {
      setError(t('steps.logo.errorSaving'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <IconImage size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-primary mb-2">
          {t('steps.logo.title')}
        </h2>
        <p className="text-muted">
          {t('steps.logo.description')}
        </p>
        <span className="inline-block mt-2 px-3 py-1 bg-[rgb(var(--surface-inset-rgb)/0.5)] rounded-full text-muted text-sm">
          {t('common.optional')}
        </span>
      </div>

      {/* Upload Area */}
      <div className="space-y-6">
        {/* Logo Preview or Upload Zone */}
        {logoUrl ? (
          <div className="relative">
            <div className="bg-white rounded-xl p-6 flex items-center justify-center shadow-lg">
              <img
                src={logoUrl}
                alt="Uploaded logo"
                className="max-h-32 max-w-full object-contain"
              />
            </div>
            <div className="absolute -top-2 -right-2">
              <span className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full shadow-lg">
                <IconSuccess size={20} className="text-primary" />
              </span>
            </div>
            <button
              type="button"
              onClick={handleRemoveLogo}
              className="absolute -bottom-2 -right-2 flex items-center justify-center w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full shadow-lg transition-colors"
            >
              <IconDelete className="text-primary" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
              ${isUploading 
                ? 'border-indigo-500 bg-indigo-500/10' 
                : 'border-strong hover:border-indigo-500 hover:bg-[rgb(var(--surface-raised-rgb)/0.5)]'
              }
            `}
          >
            {isUploading ? (
              <div className="space-y-4">
                <div className="w-12 h-12 mx-auto border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-primary">{t('steps.logo.uploading')}</p>
                <div className="w-full bg-surface-inset rounded-full h-2">
                  <div
                    className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            ) : (
              <>
                <IconUpload size={48} className="mx-auto text-faint mb-4" />
                <p className="text-primary font-medium mb-2">
                  {t('steps.logo.dropzone')}
                </p>
                <p className="text-muted text-sm">
                  {t('steps.logo.formats')}
                </p>
              </>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        {error && (
          <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-4">
            <p className="text-[var(--tone-danger-fg)] text-sm">{error}</p>
          </div>
        )}

        {/* Benefits */}
        <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-4 border border-hairline">
          <p className="text-secondary text-sm font-medium mb-2">
            {t('steps.logo.benefits')}
          </p>
          <ul className="space-y-1 text-muted text-sm">
            <li>✨ {t('steps.logo.benefit1')}</li>
            <li>📄 {t('steps.logo.benefit2')}</li>
            <li>🎨 {t('steps.logo.benefit3')}</li>
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 text-muted hover:text-primary transition-colors"
          >
            {t('common.back')}
          </button>
          
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onSkip}
              className="px-6 py-3 text-muted hover:text-primary border border-strong rounded-xl hover:border-gray-500 transition-colors"
            >
              {t('common.skip')}
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={isSaving || isUploading}
              className="px-8 py-3 bg-indigo-600 text-[#0C1322] font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-indigo-500/30"
            >
              {isSaving ? t('common.saving') : t('common.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoStep;



